import moment from 'moment-timezone';

class StudentTaxInvoiceModel {
    /**
     * @param {Object} data
     * @param {number}      data.contract_id
     * @param {number}      data.user_id
     * @param {number}      data.center_id
     * @param {string}      data.ten_khach_hang
     * @param {string}      data.so_dien_thoai
     * @param {string}      data.trung_tam
     * @param {number}      data.tong_gia_khoa
     * @param {number}      data.tong_so_tien_dong
     * @param {number}      data.tong_gia_tri_hop_dong
     * @param {string}      data.khoa_hoc_1
     * @param {string}      data.khoa_hoc_2
     * @param {string}      data.khoa_hoc_3
     * @param {string}      data.khoa_hoc_4
     * @param {string}      data.khoa_hoc_5
     * @param {string}      data.khoa_hoc_6
     * @param {string}      data.khoa_hoc_7
     * @param {string}      data.khoa_hoc_8
     * @param {string|Date} data.invoiced_at
     * @param {string}      data.nguoi_chuyen_nhuong_id
     * @param {string}      data.ten_nguoi_chuyen_nhuong
     * @param {string}      data.so_dien_thoai_nguoi_chuyen_nhuong
     */
    constructor(data = {}) {
        this.contract_id = data.contract_id ?? null;
        this.user_id = data.user_id ? data.user_id + 100000 : null;
        this.center_id = data.center_id ?? null;
        this.ten_khach_hang = data.ten_khach_hang ?? null;
        this.so_dien_thoai = data.so_dien_thoai ?? null;
        this.trung_tam = data.trung_tam ?? null;
        this.tong_gia_khoa = data.tong_gia_khoa != null ? Number(data.tong_gia_khoa) : 0;
        this.tong_so_tien_dong = data.tong_so_tien_dong != null ? Number(data.tong_so_tien_dong) : 0;
        this.tong_gia_tri_hop_dong = data.tong_gia_tri_hop_dong != null ? Number(data.tong_gia_tri_hop_dong) : 0;
        this.khoa_hoc_1 = data.khoa_hoc_1 ?? '';
        this.khoa_hoc_2 = data.khoa_hoc_2 ?? '';
        this.khoa_hoc_3 = data.khoa_hoc_3 ?? '';
        this.khoa_hoc_4 = data.khoa_hoc_4 ?? '';
        this.khoa_hoc_5 = data.khoa_hoc_5 ?? '';
        this.khoa_hoc_6 = data.khoa_hoc_6 ?? '';
        this.khoa_hoc_7 = data.khoa_hoc_7 ?? '';
        this.khoa_hoc_8 = data.khoa_hoc_8 ?? '';
        this.invoiced_at = data.invoiced_at ? moment(data.invoiced_at).format('DD/MM/YYYY') : null;
        this.nguoi_chuyen_nhuong_id = data.nguoi_chuyen_nhuong_id ? data.nguoi_chuyen_nhuong_id + 100000 : null;
        this.ten_nguoi_chuyen_nhuong = data.ten_nguoi_chuyen_nhuong ?? '';
        this.so_dien_thoai_nguoi_chuyen_nhuong = data.so_dien_thoai_nguoi_chuyen_nhuong ?? '';
    }

    get da_phat_hanh() {
        return this.invoiced_at !== null;
    }

    get trang_thai_text() {
        return this.da_phat_hanh ? 'Đã phát hành' : 'Chưa phát hành';
    }

    /**
     * Tạo instance từ row DB (khoa_hoc có thể là JSON string)
     * @param {Object} row
     * @returns {StudentTaxInvoiceModel}
     */
    static fromDB(row) {
        return new StudentTaxInvoiceModel(row);
    }

    /**
     * Tạo nhiều instance từ mảng rows DB
     * @param {Object[]} rows
     * @returns {StudentTaxInvoiceModel[]}
     */
    static fromDBList(rows) {
        return rows.map((row) => StudentTaxInvoiceModel.fromDB(row));
    }

    // ─── Static ─────────────────────────────────────────────────────────────────

    /**
     * Chuyển về plain object để lưu DB / trả về API
     * @returns {Object}
     */
    toJSON() {
        return {
            contract_id: this.contract_id,
            user_id: this.user_id,
            center_id: this.center_id,
            ten_khach_hang: this.ten_khach_hang,
            so_dien_thoai: this.so_dien_thoai,
            trung_tam: this.trung_tam,
            tong_gia_khoa: this.tong_gia_khoa,
            tong_so_tien_dong: this.tong_so_tien_dong,
            tong_gia_tri_hop_dong: this.tong_gia_tri_hop_dong,
            khoa_hoc_1: this.khoa_hoc_1,
            khoa_hoc_2: this.khoa_hoc_2,
            khoa_hoc_3: this.khoa_hoc_3,
            khoa_hoc_4: this.khoa_hoc_4,
            khoa_hoc_5: this.khoa_hoc_5,
            khoa_hoc_6: this.khoa_hoc_6,
            khoa_hoc_7: this.khoa_hoc_7,
            khoa_hoc_8: this.khoa_hoc_8,
            invoiced_at: moment(this.invoiced_at).format('DD/MM/YYYY') ?? null,
            nguoi_chuyen_nhuong_id: this.nguoi_chuyen_nhuong_id,
            ten_nguoi_chuyen_nhuong: this.ten_nguoi_chuyen_nhuong,
            so_dien_thoai_nguoi_chuyen_nhuong: this.so_dien_thoai_nguoi_chuyen_nhuong,
        };
    }

    /**
     * Chuyển về format phẳng cho export Excel
     * @returns {Object}
     */
    toExcelRow() {
        return {
            'Mã học viên': this.user_id,
            'Họ tên': this.ten_khach_hang,
            'Số điện thoại': this.so_dien_thoai,
            'Trung tâm': this.trung_tam,
            'Mã hợp đồng': this.contract_id,
            'Tổng giá khóa': this.tong_gia_khoa,
            'Tổng nạp/rút': this.tong_so_tien_dong,
            'Tổng giá trị HĐ': this.tong_gia_tri_hop_dong,
            'Trạng thái': this.trang_thai_text,
            'Ngày phát hành': moment(this.invoiced_at).format('DD/MM/YYYY') ?? '',
            'Khóa học 1': this.khoa_hoc_1,
            'Khóa học 2': this.khoa_hoc_2,
            'Khóa học 3': this.khoa_hoc_3,
            'Khóa học 4': this.khoa_hoc_4,
            'Khóa học 5': this.khoa_hoc_5,
            'Khóa học 6': this.khoa_hoc_6,
            'Khóa học 7': this.khoa_hoc_7,
            'Khóa học 8': this.khoa_hoc_8,
        };
    }
}

export default StudentTaxInvoiceModel;